import { useMemo, useState } from "react";
import { Col, Row } from "../common/components/Grid";
import { useCurrentWeather } from "../reducers/weatherSlice"
import { ReactNodeDialog } from "../common/utils/dialogUtlities";
import { useTranslation } from "react-i18next";
import { Button, ButtonGroup } from "react-bootstrap";

export const useWeatherDisplay = () => {
    const { t } = useTranslation();
    const weather = useCurrentWeather();

    const [tempUnit, setTempUnit] = useState("C");
    const [pressureUnit, setPressureUnit] = useState("P");
    const [windSpeedUnit, setWindSpeedUnit] = useState('ms');

    const temperature = useMemo(() => {
        const temp = weather?.temperature;
        if (temp !== undefined && temp !== null) {
            switch (tempUnit) {
                case 'C':
                    return <>{parseFloat(temp).toFixed(1)}&nbsp;<sup>o</sup>C</>;
                case 'F':
                    return <>{((temp * 9 / 5) + 32).toFixed(0)}&nbsp;F</>;
                default:
                    throw new Error('Unknown temperature unit - expecting C or F but got ' + tempUnit);
            }
        } else {
            return <>{t('unknown')}</>
        }
    }, [t, weather, tempUnit]);

    const pressure = useMemo(() => {
        const pressure = weather?.pressure;
        if (pressure !== undefined && pressure !== null) {
            switch (pressureUnit) {
                case 'P':
                    return <>{(pressure * 10).toFixed(1)}&nbsp;hPa</>;
                case 'H':
                    return <>{(pressure * 0.2953).toFixed(2)}&nbsp;mmHg</>;
                default:
                    throw new Error('Unknown pressure unit - expecting P or H but got ' + pressureUnit);
            }
        } else {
            return <>{t('unknown')}</>
        }
    }, [t, weather, pressureUnit]);

    const windSpeed = useMemo(() => {
        const windSpeed = weather?.windspeed
        if (windSpeed !== undefined && windSpeed !== null) {
            switch (windSpeedUnit) {
                case 'ms':
                    return <>{parseFloat(windSpeed).toFixed(1)}&nbsp;m/s</>;
                case "kph":
                    return <>{(windSpeed * 3.6).toFixed(0)}&nbsp;km/h</>;
                case 'mph':
                    return <>{(windSpeed * 2.23694).toFixed(0)}&nbsp;mph</>;
                case 'kn':
                    return <>{(windSpeed * 1.94384).toFixed(0)}&nbsp;kn</>
                default:
                    throw new Error('Unknown wind speed unit - expecting ms, kph, mph or kn but got ' + windSpeedUnit);
            }
        } else {
            return <>{t('unknown')}</>
        }
    }, [t, weather, windSpeedUnit]);

    return {
        temperature,
        pressure,
        windSpeed,
        tempUnit,
        setTempUnit,
        pressureUnit,
        setPressureUnit,
        windSpeedUnit,
        setWindSpeedUnit,
    };
}

export const WindHeading = () => {
    const weather = useCurrentWeather();
    const { t } = useTranslation();

    return <Row>
        <Col className="text-end mx-2 my-1" xs={3}>
            {t('windHeading')}
        </Col>
        <Col className="mx-2 my-1" xs>
            { weather?.windheading ? <>{weather?.windheading}<sup>o</sup></> : t('unknown') }
        </Col>
    </Row>
} 

export const WindSpeed = () => {
    const { windSpeed, windSpeedUnit, setWindSpeedUnit } = useWeatherDisplay();
    const { t } = useTranslation();

    return  <Row>
        <Col className="text-end mx-2 my-1" xs={3}>
            {t('windSpeed')}
        </Col>
        <Col className="mx-2" xs>
            <Row>
                <Col className="my-1" xs>
                    {windSpeed}
                </Col>
                <Col xs="auto">
                    <ButtonGroup size="sm">
                        <Button variant="light" active={windSpeedUnit === 'ms'} onClick={() => setWindSpeedUnit('ms')}>m/s</Button>
                        <Button variant="light" active={windSpeedUnit === 'kph'} onClick={() => setWindSpeedUnit('kph')}>km/h</Button>
                        <Button variant="light" active={windSpeedUnit === 'mph'} onClick={() => setWindSpeedUnit('mph')}>mph</Button>
                        <Button variant="light" active={windSpeedUnit === 'kn'} onClick={() => setWindSpeedUnit('kn')}>kn</Button>
                    </ButtonGroup>
                </Col>
            </Row>
        </Col>
    </Row>
}

export const Humidity = () => {
    const weather = useCurrentWeather();
    const { t } = useTranslation();

    return <Row>
        <Col className="text-end mx-2 my-1" xs={3}>
            {t('humidity')}
        </Col>
        <Col className="mx-2 my-1" xs>
            {weather?.humidity ? weather.humidity + '%' : t('unknown')}
        </Col>
    </Row>
       
}

export const Pressure = () => {
    const { pressure, pressureUnit, setPressureUnit } = useWeatherDisplay();
    const { t } = useTranslation();

    return <Row>
        <Col className="text-end mx-2 my-1" xs={3}>
            {t('weatherPressure')}
        </Col>
        <Col className="mx-2" xs>
            <Row>
                <Col className="my-1" xs>
                    {pressure}
                </Col>
                <Col xs="auto">
                    <ButtonGroup size="sm">
                        <Button variant="light" active={pressureUnit === 'P'} onClick={() => setPressureUnit('P')}>hPa</Button>
                        <Button variant="light" active={pressureUnit === 'H'} onClick={() => setPressureUnit('H')}>inHg</Button>
                    </ButtonGroup>
                </Col>
            </Row>
        </Col>
    </Row>
}

const WeatherInfo = () => {
    const { temperature, tempUnit, setTempUnit } = useWeatherDisplay();
    const [showDetails, setShowDetails] = useState(false);
    const weather = useCurrentWeather();
    const { t } = useTranslation();

    return <>
        <ReactNodeDialog title={t('currentWeather')} showModal={showDetails} onClose={() => setShowDetails(false)}>
            <Row>
                <Col className="text-end mx-2 my-1" xs>
                    {t('weatherConditions')}
                </Col>
                <Col className="mx-2 my-1" xs>
                    { weather?.icon && <div className={`wi ${weather.icon}`} style={{ fontSize: '1.5em' }}></div> } { weather?.condition }
                </Col>
            </Row>
            <Row>
                <Col className="text-end mx-2 my-1" xs>
                    {t('temperature')}
                </Col>
                <Col className="mx-2" xs>
                    <Row>
                        <Col className="my-1" xs>
                            {temperature}
                        </Col>
                        <Col xs="auto">
                            <ButtonGroup size="sm">
                                <Button variant="light" active={tempUnit === 'C'} onClick={() => setTempUnit('C')}><sup>o</sup>C</Button>
                                <Button variant="light" active={tempUnit === 'F'} onClick={() => setTempUnit('F')}>F</Button>
                            </ButtonGroup>
                        </Col>
                    </Row>
                </Col>
            </Row>
            
            <Humidity></Humidity>
            <WindSpeed></WindSpeed>
            <WindHeading></WindHeading>
        </ReactNodeDialog>
        { weather && <button type="button" className={weather?.alert ? 'bg-daner' : null}  onClick={() => setShowDetails(true)} style={{ backgroundColor: 'transparent', border: 'none', textAlign: 'start', color: 'white' }}>
            <Row>
                <Col style={{ fontWeight: 'bold' }}>
                    {weather?.location}
                </Col>
            </Row>
            <Row>
                <Col xs="auto" style={{ fontSize: 'large', marginTop: 'auto', marginBottom: 'auto' }}>
                    { weather?.icon && <div className={`wi ${weather.icon}`}></div> }
                </Col>
                <Col className="px-2" xs>
                    <Row>
                        <Col>
                            {temperature}
                        </Col>
                    </Row>
                    <Row>
                        <Col style={{ fontSize: '0.7em' }}>
                            {weather?.condition}
                        </Col>
                    </Row>
                </Col>
            </Row>
        </button> }
    </>
}

export default WeatherInfo;